import { FC, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useState } from 'react';
import { TeamMember, TeamTaskMemberSummary } from '@demind-inc/core';
import { CheckRounded } from '@mui/icons-material';
import { Menu, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import './TeamTaskAssigneeSelect.scss';
import { mdiUnfoldMoreHorizontal } from '@mdi/js';
import Icon from '@mdi/react';
import TeamMemberAvatar from '../TeamMemberAvatar';
import { getTeamMemberInitials } from '../utils';

type TeamTaskAssigneeMember = TeamTaskMemberSummary & Pick<Partial<TeamMember>, 'status'>;

interface TeamTaskAssigneeSelectProps {
  members: TeamTaskAssigneeMember[];
  currentUserId: string;
  value: string;
  disabled?: boolean;
  compact?: boolean;
  variant?: 'default' | 'modal';
  onChange: (assigneeUserId: string) => void;
}

const TeamTaskAssigneeSelect: FC<TeamTaskAssigneeSelectProps> = ({
  members,
  currentUserId,
  value,
  disabled = false,
  compact = false,
  variant = 'default',
  onChange,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedMember = members.find((member) => member.userId === value);
  const getAssigneeDisplayName = (member: TeamTaskAssigneeMember, index: number) => {
    return member.name?.trim() || t('team.common.memberWithIndex', { index: index + 1 });
  };
  const selectedName = selectedMember
    ? getAssigneeDisplayName(selectedMember, 0)
    : t('team.common.unassigned');
  const selectedInitials = selectedMember ? getTeamMemberInitials(selectedName) : 'UA';
  const selectedPhotoUrl = selectedMember?.photoUrl || '';
  const activeMembers = members.filter((member) => !member.status || member.status === 'active');
  const selectedDisplayName = selectedName.replace(/\s*\(You\)\s*$/, '');

  const renderAvatar = (name: string, initials: string, photoUrl?: string) => (
    <TeamMemberAvatar
      name={name}
      initials={initials}
      photoUrl={photoUrl}
      size={variant === 'modal' ? 'small' : 'xsmall'}
      className={variant === 'modal' ? 'team-task-assignee-select-modal__avatar' : undefined}
    />
  );
  const renderEmptyAvatar = () => (
    <span className="team-task-assignee-select__empty-avatar" aria-hidden="true" />
  );
  const stopAssigneeEvent = (event: ReactMouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };
  const handleOpenMenu = (event: ReactMouseEvent<HTMLButtonElement>) => {
    stopAssigneeEvent(event);

    if (disabled) {
      return;
    }

    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = (event?: object) => {
    if (event && 'stopPropagation' in event) {
      (event as { stopPropagation: () => void }).stopPropagation();
    }

    setAnchorEl(null);
  };
  const handleSelectAssignee = (assigneeUserId: string) => {
    onChange(assigneeUserId);
    handleCloseMenu();
  };

  useEffect(() => {
    if (!anchorEl) {
      return undefined;
    }

    const handleDocumentClickCapture = (event: globalThis.MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (anchorEl.contains(target)) {
        return;
      }

      const menuPaper = document.querySelector('.team-task-assignee-select__menu-paper');

      if (menuPaper?.contains(target)) {
        return;
      }

      setAnchorEl(null);
      event.stopPropagation();
    };

    document.addEventListener('click', handleDocumentClickCapture, true);

    return () => {
      document.removeEventListener('click', handleDocumentClickCapture, true);
    };
  }, [anchorEl]);

  if (variant === 'modal') {
    const handleModalChange = (event: SelectChangeEvent<string>) => {
      onChange(event.target.value);
    };

    return (
      <Select
        className="team-task-assignee-select-modal"
        size="small"
        value={value}
        onChange={handleModalChange}
        displayEmpty
        disabled={disabled}
        aria-label={t('team.tasks.assignee')}
        IconComponent={() => (
          <Icon
            path={mdiUnfoldMoreHorizontal}
            size="16px"
            className="team-task-assignee-select-modal__icon"
          />
        )}
        renderValue={() => (
          <div className="team-task-assignee-select-modal__value">
            {selectedMember
              ? renderAvatar(selectedDisplayName, selectedInitials, selectedPhotoUrl)
              : renderEmptyAvatar()}
            <span
              className={clsx(
                'team-task-assignee-select-modal__name',
                !selectedMember && 'team-task-assignee-select-modal__name--empty'
              )}
            >
              {selectedDisplayName}
            </span>
          </div>
        )}
        MenuProps={{
          PaperProps: {
            className: 'team-task-assignee-select-modal__menu-paper',
          },
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem value="" className="team-task-assignee-select-modal__item">
          <div className="team-task-assignee-select-modal__item-content">
            {renderEmptyAvatar()}
            <span className="team-task-assignee-select-modal__name team-task-assignee-select-modal__name--empty">
              {t('team.common.unassigned')}
            </span>
          </div>
        </MenuItem>
        {activeMembers.map((member, index) => {
          const memberName = getAssigneeDisplayName(member, index);
          const memberDisplayName = memberName.replace(/\s*\(You\)\s*$/, '');

          return (
            <MenuItem
              key={member.teamMemberId || member.userId}
              value={member.userId}
              className="team-task-assignee-select-modal__item"
            >
              <div className="team-task-assignee-select-modal__item-content">
                {renderAvatar(
                  memberDisplayName,
                  getTeamMemberInitials(memberName),
                  member.photoUrl
                )}
                <span>{memberDisplayName}</span>
              </div>
            </MenuItem>
          );
        })}
      </Select>
    );
  }

  const renderMenuItem = ({
    assigneeUserId,
    label,
    avatar,
  }: {
    assigneeUserId: string;
    label: string;
    avatar: ReactNode;
  }) => {
    const isSelected = value === assigneeUserId;

    return (
      <MenuItem
        key={assigneeUserId || 'unassigned'}
        className={clsx(
          'team-task-assignee-select__menu-item',
          isSelected && 'team-task-assignee-select__menu-item--selected'
        )}
        onClick={(event) => {
          event.stopPropagation();
          handleSelectAssignee(assigneeUserId);
        }}
      >
        <span className="team-task-assignee-select__menu-item-content">
          {avatar}
          <Typography
            variant="subtitle2"
            component="span"
            className={clsx(
              'team-task-assignee-select__menu-item-name',
              !assigneeUserId && 'team-task-assignee-select__menu-item-name--empty'
            )}
          >
            {label}
          </Typography>
        </span>
        {isSelected && <CheckRounded className="team-task-assignee-select__menu-check" />}
      </MenuItem>
    );
  };

  return (
    <>
      <button
        type="button"
        className={clsx(
          'team-task-assignee-select',
          compact && 'team-task-assignee-select--compact',
          disabled && 'team-task-assignee-select--disabled'
        )}
        disabled={disabled}
        aria-label={t('team.tasks.assignee')}
        aria-haspopup="menu"
        aria-expanded={!!anchorEl}
        onMouseDown={stopAssigneeEvent}
        onClick={handleOpenMenu}
      >
        {selectedMember
          ? renderAvatar(selectedDisplayName, selectedInitials, selectedPhotoUrl)
          : renderEmptyAvatar()}
        {!compact && (
          <Typography
            variant="subtitle2"
            component="span"
            className={clsx(
              'team-task-assignee-select__name',
              !selectedMember && 'team-task-assignee-select__name--empty'
            )}
          >
            {selectedDisplayName}
          </Typography>
        )}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleCloseMenu}
        className="team-task-assignee-select__menu"
        PaperProps={{
          className: 'team-task-assignee-select__menu-paper',
          onMouseDown: (event) => event.stopPropagation(),
          onClick: (event) => event.stopPropagation(),
        }}
        MenuListProps={{
          dense: true,
          onClick: (event) => event.stopPropagation(),
        }}
      >
        {renderMenuItem({
          assigneeUserId: '',
          label: t('team.common.unassigned'),
          avatar: renderEmptyAvatar(),
        })}
        {activeMembers.map((member, index) => {
          const memberName = getAssigneeDisplayName(member, index);
          const memberDisplayName = memberName.replace(/\s*\(You\)\s*$/, '');

          return renderMenuItem({
            assigneeUserId: member.userId,
            label: memberDisplayName,
            avatar: renderAvatar(
              memberDisplayName,
              getTeamMemberInitials(memberName),
              member.photoUrl
            ),
          });
        })}
      </Menu>
    </>
  );
};

export default TeamTaskAssigneeSelect;
